import type { ReactNode, ButtonHTMLAttributes, InputHTMLAttributes, TextareaHTMLAttributes } from 'react'

/**
 * UI primitives — Warm Industrial aesthetic, friendly tone, mobile-first.
 * Inputs use 16px font-size to prevent iOS zoom on focus. APIs unchanged.
 */

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`relative border border-surface-700 bg-surface-900 p-4 sm:p-5 transition hover:border-surface-400 ${className}`}
    >
      {children}
    </div>
  )
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string
  subtitle?: string
  actions?: ReactNode
}) {
  return (
    <div className="mb-6 sm:mb-8 pb-4 sm:pb-5 border-b border-surface-700 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl text-surface-50 leading-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-surface-400 mt-1.5 sm:mt-2 max-w-xl leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div className="flex flex-wrap gap-2 shrink-0">{actions}</div>}
    </div>
  )
}

export function Button({
  variant = 'primary',
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' | 'danger' }) {
  const styles = {
    primary: 'bg-accent-500 hover:bg-accent-400 text-surface-950 font-semibold',
    ghost:
      'border border-surface-700 hover:border-accent-500 hover:text-accent-400 text-surface-100 bg-transparent',
    danger: 'bg-rebar-500 hover:opacity-90 text-surface-50 font-semibold',
  }[variant]
  return (
    <button
      {...rest}
      className={`px-3 sm:px-4 py-2 text-[13px] sm:text-sm disabled:opacity-40 disabled:cursor-not-allowed transition ${styles} ${className}`}
    />
  )
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string
  children: ReactNode
  hint?: string
}) {
  return (
    <label className="block">
      <span className="text-sm text-surface-100">{label}</span>
      {children}
      {hint && (
        <span className="block text-xs text-surface-400 mt-1.5 leading-relaxed">{hint}</span>
      )}
    </label>
  )
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`mt-2 w-full bg-surface-950 border border-surface-700 px-3 py-2.5 text-base sm:text-sm text-surface-100 focus:border-accent-500 focus:outline-none transition ${props.className ?? ''}`}
    />
  )
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={`mt-2 w-full bg-surface-950 border border-surface-700 px-3 py-2.5 text-base sm:text-sm text-surface-100 focus:border-accent-500 focus:outline-none transition ${props.className ?? ''}`}
    />
  )
}

export function Select({
  children,
  ...rest
}: InputHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  return (
    <select
      {...(rest as object)}
      className={`mt-2 w-full bg-surface-950 border border-surface-700 px-3 py-2.5 text-base sm:text-sm text-surface-100 focus:border-accent-500 focus:outline-none transition ${rest.className ?? ''}`}
    >
      {children}
    </select>
  )
}

export function Empty({ message, hint }: { message: string; hint?: string }) {
  return (
    <div className="border border-dashed border-surface-700 px-4 sm:px-5 py-6 sm:py-8 text-center">
      <p className="text-surface-100">{message}</p>
      {hint && <p className="text-sm text-surface-400 mt-2">{hint}</p>}
    </div>
  )
}

export function ErrorBanner({ error }: { error: unknown }) {
  let msg = error instanceof Error ? error.message : 'Unknown error'
  const body = (error as { body?: unknown })?.body
  if (body && typeof body === 'object') {
    const fromError = (body as { error?: unknown }).error
    const fromMessage = (body as { message?: unknown }).message
    const detail = (body as { details?: unknown }).details
    if (typeof fromError === 'string' && fromError) msg = fromError
    else if (typeof fromMessage === 'string' && fromMessage) msg = fromMessage
    if (detail && typeof detail === 'object') {
      try {
        msg = `${msg} — ${JSON.stringify(detail)}`
      } catch {
        // ignore
      }
    }
  }
  return (
    <div className="border-l-2 border-rebar-500 bg-rebar-500/5 px-3 sm:px-4 py-2.5 sm:py-3 flex gap-3 items-start">
      <span className="text-rebar-500 shrink-0 text-base leading-none mt-0.5">!</span>
      <span className="text-sm text-surface-100 break-words">{msg}</span>
    </div>
  )
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 py-4">
      <span
        className="inline-block w-2 h-2 bg-accent-500 animate-pulse"
        style={{ animationDuration: '0.8s' }}
      />
      <span className="text-sm text-surface-400">{label}</span>
    </div>
  )
}

export function Tag({
  children,
  tone = 'default',
}: {
  children: ReactNode
  tone?: 'default' | 'success' | 'warn' | 'danger'
}) {
  const styles = {
    default: 'border-surface-700 text-surface-100 bg-surface-900',
    success: 'border-accent-500/50 text-accent-400 bg-accent-500/10',
    warn: 'border-concrete-500/40 text-concrete-500 bg-concrete-500/10',
    danger: 'border-rebar-500/40 text-rebar-500 bg-rebar-500/10',
  }[tone]
  return (
    <span
      className={`inline-block border px-2 py-0.5 text-[12px] capitalize ${styles}`}
    >
      {children}
    </span>
  )
}

export function Currency({ amount }: { amount: number | null | undefined }) {
  if (amount === null || amount === undefined) return <span className="text-surface-400">—</span>
  return (
    <span className="font-mono tabular-nums">
      ₹{Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
    </span>
  )
}

export function FormattedDate({ ts }: { ts: number | null | undefined }) {
  if (!ts) return <span className="text-surface-400">—</span>
  return (
    <span className="font-mono text-[12px] tabular-nums">
      {new Date(ts * 1000).toLocaleString()}
    </span>
  )
}
